import type { DispatchLibrary } from "#public"

import { CreateTypedUseTranslation } from "./CreateUseTranslation"

type CtxOf<F> = F extends () => string
  ? undefined
  : F extends (ctx: infer C) => string
    ? C
    : undefined

type TransProps<
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
> = {
  [Lib in keyof Names & string]: {
    [Name in keyof Names[Lib] & string]: {
      library: Lib
      name: Name
      context?: CtxOf<Names[Lib][Name]>
      language?: Lang
      // Slot rendered in place of the translated string when `name` resolves
      // — lets a caller gate auxiliary UI on a translation's existence.
      children?: preact.ComponentChildren
    }
  }[keyof Names[Lib] & string]
}[keyof Names & string]

export const CreateTypedTranslation = <
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
>(
  lib: DispatchLibrary<Names, Lang>,
  lang?: NoInfer<Lang> | (() => NoInfer<Lang> | undefined)
) => {
  const useTranslation = CreateTypedUseTranslation(lib, lang)

  return (props: TransProps<Names, Lang>) => {
    const names = useTranslation(props.library, props.language)
    // `TransProps` guarantees `name`/`context` line up per caller, but TS
    // can't carry each Name's own signature through a runtime string index —
    // the one cast this needs.
    const fn = (names as Record<string, ((ctx?: unknown) => string) | undefined>)[props.name]
    if (!fn) return null
    return <>{props.children ?? fn(props.context)}</>
  }
}

// Same runtime as `CreateTypedTranslation`, just cast to a looser prop type:
// `name`/`context` are open string/unknown instead of the closed per-key
// union above, for callers whose key is itself dynamic (a field id, an
// entity name, an error enum) and can't be a `TransProps` literal.
type LooseTransProps<
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
> = {
  library: keyof Names & string
  name: string
  context?: unknown
  language?: Lang
  children?: preact.ComponentChildren
}

export const CreateTranslation = CreateTypedTranslation as <
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
>(
  lib: DispatchLibrary<Names, Lang>,
  lang?: NoInfer<Lang> | (() => NoInfer<Lang> | undefined)
) => (props: LooseTransProps<Names, Lang>) => preact.JSX.Element | null
